import styled from 'styled-components';
import dayjs from 'dayjs';

import IconPageArrow from 'assets/icons/IconPageArrow';
import IconFilter from 'assets/icons/IconFilter';

import { getSpecificDateFrom } from 'utils/formatting';
import { Option } from 'types/common.types';

import { TicketFilterInput } from './hooks/useBookingListHooks';
import { HEADER_HEIGHT } from 'constants/style';

import { IconCalendar, IconList } from 'assets/icons';
import { useIsMobile } from 'hooks/common';

const BookingHeaderBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  position: sticky;
  top: ${HEADER_HEIGHT}px;
  z-index: 15;

  flex-wrap: wrap;
  row-gap: 12px;

  width: 100%;
  padding: 24px 0px;
  /* padding-bottom: 32px; */

  background-color: #fff;

  @media (max-width: 768px) {
    padding-bottom: 24px;

    top: 0px;
  }
`;

const BookingControlBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  > div[data-type='prev'],
  div[data-type='next'] {
    cursor: pointer;

    width: 20px;
    height: 20px;

    > svg {
      width: 100%;
      height: 100%;
    }
  }

  > div[data-type='next'] {
    transform: rotate(180deg);
  }
`;

const SuffixBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  color: #989ba2;
`;

const BookingMonthBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  p[data-type='month'] {
    color: rgba(27, 28, 30, 1);
    font-weight: 600;
    font-size: 20px;
    line-height: 28px;

    vertical-align: middle;
    text-decoration: underline;
    text-decoration-style: solid;
    text-decoration-thickness: 2px;
    text-underline-offset: 4px;

    cursor: pointer;
  }

  > p[data-type='total'] {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;

    color: rgba(152, 155, 162, 1);
  }
`;

const PendingUseRow = styled.div`
  display: flex;
  justify-content: flex-end;

  width: 100%;
`;

const FilterBox = styled.div<{ $isApplied?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;

  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: ${({ $isApplied }) =>
    $isApplied ? 'rgba(0, 113, 233, 1)' : 'rgba(112, 115, 124, 1)'};

  cursor: pointer;
`;

interface Props {
  selectedMonth: Option;
  handleSelectingMonthModal: () => void;
  handleProductFilterModal: () => void;
  handleSelectingMonth: (opt: Option) => void;
  hostCreatedAt: dayjs.Dayjs;
  totalBookingCount: number;
  currentFilter: TicketFilterInput['filter'];
  handleListType: () => void;
  listType: 'calendar' | 'list';
  isLoading: boolean;
  onlyPendingUse: boolean;
  handleOnlyPendingUse: () => void;
}

function BookingScheduleHeader({
  selectedMonth,
  handleSelectingMonthModal,
  handleProductFilterModal,
  handleSelectingMonth,
  hostCreatedAt,
  totalBookingCount,
  currentFilter,
  handleListType,
  isLoading,
  listType,
  onlyPendingUse,
  handleOnlyPendingUse,
}: Props) {
  const currentDate = dayjs();
  const selectedDate = dayjs(parseInt(selectedMonth.value));
  const isMobile = useIsMobile();

  const currentYear = currentDate.year();
  const selectedYear = selectedDate.year();

  const isSameYear = currentYear === selectedYear;

  const hasPrevMonth = selectedDate.isAfter(hostCreatedAt, 'month');
  const hasNextMonth = selectedDate.isBefore(
    currentDate.add(3, 'month'),
    'month',
  );

  const isAppliedProductFilter = !!currentFilter.productId;

  // 사용처리 누락 보기는 목록 보기 전용. 총 건수는 캘린더 기준이라 이때는 숨긴다
  const isPendingUseView = listType === 'list' && onlyPendingUse;

  const pendingUseToggle = (
    <FilterBox onClick={handleOnlyPendingUse} $isApplied={onlyPendingUse}>
      사용처리 안 된 예약만
    </FilterBox>
  );

  const onClickPrevMonth = () => {
    if (!hasPrevMonth || isLoading) {
      return;
    }

    const prevMonth = selectedDate.subtract(1, 'month');

    const label = prevMonth.format('YYYY년 M월');
    const value = prevMonth.valueOf().toString();

    handleSelectingMonth({
      label,
      value,
    });
  };

  const onClickNextMonth = () => {
    if (!hasNextMonth || isLoading) {
      return;
    }

    const nextMonth = selectedDate.add(1, 'month');

    const label = nextMonth.format('YYYY년 M월');
    const value = nextMonth.valueOf().toString();

    handleSelectingMonth({
      label,
      value,
    });
  };

  return (
    <BookingHeaderBox>
      <BookingControlBox>
        <div data-type="prev" onClick={onClickPrevMonth}>
          <IconPageArrow
            color={
              hasPrevMonth ? 'rgba(112, 115, 124, 1)' : 'rgba(219, 220, 223, 1)'
            }
          />
        </div>

        <BookingMonthBox>
          <p data-type="month" onClick={handleSelectingMonthModal}>
            {!isSameYear && (
              <>
                {getSpecificDateFrom(selectedMonth.label, 'YY년')}
                &nbsp;
              </>
            )}
            {getSpecificDateFrom(selectedMonth.label, 'M월')}
          </p>
          {!isPendingUseView && (
            <p data-type="total">총 {totalBookingCount || 0}건</p>
          )}
        </BookingMonthBox>

        <div data-type="next" onClick={onClickNextMonth}>
          <IconPageArrow
            color={
              hasNextMonth ? 'rgba(112, 115, 124, 1)' : 'rgba(219, 220, 223, 1)'
            }
          />
        </div>
      </BookingControlBox>

      <SuffixBox>
        {/* 모바일은 한 줄에 다 들어가지 않아 아래 줄(PendingUseRow)에 둔다 */}
        {listType === 'list' && !isMobile && (
          <>
            {pendingUseToggle}

            {`\u2502`}
          </>
        )}

        <FilterBox
          onClick={handleProductFilterModal}
          $isApplied={isAppliedProductFilter}
        >
          <IconFilter
            width={20}
            height={20}
            color={isAppliedProductFilter ? 'rgba(0, 113, 233, 1)' : '#989BA2'}
          />
          필터 {isAppliedProductFilter && '적용'}
        </FilterBox>

        {`\u2502`}

        <FilterBox onClick={handleListType}>
          {listType === 'calendar' ? (
            <IconList color="#989BA2" width={20} height={20} />
          ) : (
            <IconCalendar width={20} height={20} color="#989BA2" />
          )}
          {listType === 'calendar'
            ? isMobile
              ? '목록'
              : '목록으로 보기'
            : isMobile
            ? '캘린더'
            : '캘린더로 보기'}
        </FilterBox>
      </SuffixBox>

      {listType === 'list' && isMobile && (
        <PendingUseRow>{pendingUseToggle}</PendingUseRow>
      )}
    </BookingHeaderBox>
  );
}

export default BookingScheduleHeader;
